import { OwaDate } from 'owa-datetime';
import { ActionSource as LegacyActionSource } from 'owa-mail-store';
import { InstrumentationContext } from 'owa-mail-store';
import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import FlagType from 'owa-service/lib/contract/FlagType';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import Message from 'owa-service/lib/contract/Message';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { trace } from 'owa-trace';
import {
    getFocusedFilterForTable,
    listViewStore,
    TableView,
    TableQueryType,
    getSortByForTable,
    MailRowDataPropertyGetter,
    getViewFilterForTable,
} from 'owa-mail-list-store';
import { ActionSource } from 'owa-analytics-actions';

export function getSortColumnToLog(tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    let sortColumn = null;

    if (tableView) {
        const isSortBySupported =
            tableView.tableQuery.type == TableQueryType.Folder ||
            tableView.tableQuery.type == TableQueryType.Group;
        sortColumn = isSortBySupported ? getSortByForTable(tableView).sortColumn : null;
    }

    return sortColumn;
}

export default {
    blockSendersFromTable: {
        name: 'TnS_BlockSendersFromListView',
        customData: (rowKeys: string[], tableView: TableView, actionSource: LegacyActionSource) => [
            actionSource,
            rowKeys.length,
            tableView.tableQuery.type,
        ],
    },
    setConversationsFlagState: {
        name: (
            conversationRowKeys: string[],
            instrumentationContexts: InstrumentationContext[],
            tableViewId: string,
            flagTypeToApply: FlagType,
            actionSource: ActionSource
        ) => {
            let name;
            switch (flagTypeToApply.FlagStatus) {
                case 'Flagged':
                    name = 'TnS_FlagConversation';
                    break;
                case 'NotFlagged':
                    name = 'TnS_UnflagConversation';
                    break;
                case 'Complete':
                    name = 'TnS_CompleteFlagConversation';
                    break;
                default:
                    trace.error('Cannot log invalid flag type');
            }
            return name;
        },
        customData: (
            conversationRowKeys: string[],
            instrumentationContexts: InstrumentationContext[],
            tableViewId: string,
            flagTypeToApply: FlagType,
            actionSource: ActionSource
        ) => {
            const tableView = listViewStore.tableViews.get(tableViewId);
            return [getFocusedFilterForTable(tableView), getViewFilterForTable(tableView)];
        },
        cosmosOnlyData: (
            conversationRowKeys: string[],
            instrumentationContexts: InstrumentationContext[],
            tableViewId: string,
            flagTypeToApply: FlagType,
            actionSource: ActionSource
        ) => getFlagIdsToLogFromRowKeys(conversationRowKeys, tableViewId),
        actionSource: (
            conversationRowKeys: string[],
            instrumentationContexts: InstrumentationContext[],
            tableViewId: string,
            flagTypeToApply: FlagType,
            actionSource: ActionSource
        ) => actionSource,
        options: { isCore: true },
    },
    setItemsFlagState: {
        name: (
            itemIds: string[],
            instrumentationContexts: InstrumentationContext[],
            flagTypeToApply: FlagType,
            tableViewId: string,
            actionSource: ActionSource
        ) => {
            let name;
            switch (flagTypeToApply.FlagStatus) {
                case 'Flagged':
                    name = 'TnS_FlagItem';
                    break;
                case 'NotFlagged':
                    name = 'TnS_UnflagItem';
                    break;
                case 'Complete':
                    name = 'TnS_CompleteFlagItem';
                    break;
                default:
                    trace.error('Cannot log invalid flag type');
            }
            return name;
        },
        customData: (
            itemIds: string[],
            instrumentationContexts: InstrumentationContext[],
            flagTypeToApply: FlagType,
            tableViewId: string,
            actionSource: ActionSource
        ) => {
            const tableView = listViewStore.tableViews.get(tableViewId);
            return [getFocusedFilterForTable(tableView), getViewFilterForTable(tableView)];
        },
        cosmosOnlyData: (
            itemIds: string[],
            instrumentationContexts: InstrumentationContext[],
            flagTypeToApply: FlagType,
            tableViewId: string,
            actionSource: ActionSource
        ) => JSON.stringify({ itemIds: itemIds }),
        actionSource: (
            itemIds: string[],
            instrumentationContexts: InstrumentationContext[],
            flagTypeToApply: FlagType,
            tableViewId: string,
            actionSource: ActionSource
        ) => actionSource,
        options: { isCore: true },
    },
    archiveMailListRows: {
        name: 'TnS_Archive',
        customData: (rowKeys: string[], tableView: TableView, actionSource: ActionSource) => [
            tableView.tableQuery.folderId // folderId is null in search
                ? folderIdToName(tableView.tableQuery.folderId)
                : null,
            tableView.tableQuery.type,
            tableView.tableQuery.listViewType,
            rowKeys.length,
            getFocusedFilterForTable(tableView),
            getViewFilterForTable(tableView),
        ],
        cosmosOnlyData: (rowKeys: string[], tableView: TableView, actionSource: ActionSource) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, tableView),
            });
        },
        options: { isCore: true },
        actionSource: (rowKeys: string[], tableView: TableView, actionSource: ActionSource) =>
            actionSource,
    },
    deleteConversation: {
        name: 'TnS_DeleteConversation',
        options: { isQos: true },
        customData: (
            conversationActionContexts: ApplyConversationActionContext[],
            originalTableViewId: string,
            disposalType: DisposalType,
            instrumentationContexts: InstrumentationContext[],
            actionSource?: ActionSource
        ) => {
            const tableView = listViewStore.tableViews.get(originalTableViewId);
            return [
                actionSource,
                conversationActionContexts.length,
                disposalType,
                getSortColumnToLog(originalTableViewId),
                getFocusedFilterForTable(tableView),
                getViewFilterForTable(tableView),
            ];
        },
        cosmosOnlyData: (
            conversationActionContexts: ApplyConversationActionContext[],
            originalTableViewId: string,
            disposalType: DisposalType,
            instrumentationContexts: InstrumentationContext[],
            actionSource?: ActionSource
        ) =>
            JSON.stringify({
                conversationIds: conversationActionContexts.map(context => context.conversationId),
            }),
        actionSource: (
            conversationActionContexts: ApplyConversationActionContext[],
            originalTableViewId: string,
            disposalType: DisposalType,
            instrumentationContexts: InstrumentationContext[],
            actionSource?: ActionSource
        ) => actionSource,
    },
    markItemsJunkNotJunk: {
        name: (
            itemIds: string[],
            isJunkValueToSet: boolean,
            actionSource: ActionSource,
            tableView: TableView,
            instrumentationContexts: InstrumentationContext[],
            markItemsJunkNotJunkStoreUpdate: () => void
        ) => {
            let name = 'TnS_MarkAs';
            name += isJunkValueToSet ? 'Junk' : 'NotJunk';
            return name;
        },
        customData: (
            itemIds: string[],
            isJunkValueToSet: boolean,
            actionSource: ActionSource,
            tableView: TableView,
            instrumentationContexts: InstrumentationContext[],
            markItemsJunkNotJunkStoreUpdate: () => void
        ) => [
            itemIds.length,
            getFocusedFilterForTable(tableView),
            getViewFilterForTable(tableView),
        ],
        cosmosOnlyData: (
            itemIds: string[],
            isJunkValueToSet: boolean,
            actionSource: ActionSource,
            tableView: TableView,
            instrumentationContexts: InstrumentationContext[],
            markItemsJunkNotJunkStoreUpdate: () => void
        ) => JSON.stringify({ itemIds: itemIds }),
        actionSource: (
            itemIds: string[],
            isJunkValueToSet: boolean,
            actionSource: ActionSource,
            tableView: TableView,
            instrumentationContexts: InstrumentationContext[],
            markItemsJunkNotJunkStoreUpdate: () => void
        ) => actionSource,
    },
    markItemsAsPhishing: {
        name: 'TnS_MarkAsPhishing',
        customData: (
            itemIds: string[],
            tableView: TableView,
            actionSource: ActionSource,
            markAsPhishingStoreUpdate: () => void
        ) => [itemIds.length, getFocusedFilterForTable(tableView)],
        cosmosOnlyData: (
            itemIds: string[],
            tableView: TableView,
            actionSource: ActionSource,
            markAsPhishingStoreUpdate: () => void
        ) => JSON.stringify({ itemIds: itemIds }),
        actionSource: (
            itemIds: string[],
            tableView: TableView,
            actionSource: ActionSource,
            markAsPhishingStoreUpdate: () => void
        ) => actionSource,
    },
    moveConversation: {
        name: (
            rowKeys: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => {
            let name = 'TnS_MoveConversation';
            name += !actionSource ? 'Background' : '';
            return name;
        },
        customData: (
            rowKeys: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => [
            originalTableView.tableQuery.type,
            originalTableView.tableQuery.folderId // folderId is null in search
                ? folderIdToName(originalTableView.tableQuery.folderId)
                : null,
            folderIdToName(destinationFolderId),
            rowKeys.length,
            getFocusedFilterForTable(originalTableView),
            getViewFilterForTable(originalTableView),
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, originalTableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, originalTableView),
            });
        },
        options: { isCore: true },
        actionSource: (
            rowKeys: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => actionSource,
    },
    moveItems: {
        name: (
            itemIds: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => {
            let name = 'TnS_MoveItem';
            name += !actionSource ? 'Background' : '';
            return name;
        },
        customData: (
            itemIds: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => [
            originalTableView.tableQuery.type,
            originalTableView.tableQuery.folderId // folderId is null in search
                ? folderIdToName(originalTableView.tableQuery.folderId)
                : null,
            folderIdToName(destinationFolderId),
            itemIds.length,
            getFocusedFilterForTable(originalTableView),
            getViewFilterForTable(originalTableView),
        ],
        cosmosOnlyData: (
            itemIds: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => {
            return JSON.stringify({ itemIds: itemIds });
        },
        options: { isCore: true },
        actionSource: (
            itemIds: string[],
            destinationFolderId: string,
            originalTableView: TableView,
            actionSource: ActionSource
        ) => actionSource,
    },
    copyMailListRows: {
        name: 'TnS_Copy',
        customData: (
            rowKeys: string[],
            originalTableView: TableView,
            destinationFolderId: string,
            sourceFolderId: string,
            actionSource: ActionSource
        ) => [
            originalTableView.tableQuery.type,
            originalTableView.tableQuery.folderId // folderId is null in search
                ? folderIdToName(sourceFolderId)
                : null,
            folderIdToName(destinationFolderId),
            rowKeys.length,
            getFocusedFilterForTable(originalTableView),
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            originalTableView: TableView,
            destinationFolderId: string,
            sourceFolderId: string,
            actionSource: ActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, originalTableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, originalTableView),
            });
        },
        actionSource: (
            rowKeys: string[],
            originalTableView: TableView,
            destinationFolderId: string,
            sourceFolderId: string,
            actionSource: ActionSource
        ) => actionSource,
    },

    restoreConversation: { name: 'TnS_RestoreConversation' },
    restoreItems: { name: 'TnS_RestoreItem' },
    unsubscribe: {
        name: 'TnS_Unsubscribe',
        customData: (
            message: Message,
            originalTableView: TableView,
            isTrustedUnsubscribe: boolean,
            actionSource: LegacyActionSource
        ) => [actionSource, isTrustedUnsubscribe, getFocusedFilterForTable(originalTableView)],
        cosmosOnlyData: (
            message: Message,
            originalTableView: TableView,
            isTrustedUnsubscribe: boolean,
            actionSource: LegacyActionSource
        ) => {
            return JSON.stringify({ itemId: message.ItemId.Id });
        },
    },
    optOut: { name: 'TnS_OptOut' },
    pinConversations: {
        name: (
            conversationRowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => {
            let name = 'TnS_';
            name += shouldMarkAsPinned ? 'PinConversation' : 'UnpinConversation';
            return name;
        },
        customData: (
            conversationRowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => [
            conversationRowKeys.length,
            getFocusedFilterForTable(tableView),
            getViewFilterForTable(tableView),
        ],
        cosmosOnlyData: (
            conversationRowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(conversationRowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(conversationRowKeys, tableView),
            });
        },
        actionSource: (
            conversationRowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => actionSource,
        options: { isCore: true },
    },
    pinItems: {
        name: (
            rowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => {
            let name = 'TnS_';
            name += shouldMarkAsPinned ? 'PinItem' : 'UnpinItem';
            return name;
        },
        customData: (
            rowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => [
            rowKeys.length,
            getFocusedFilterForTable(tableView),
            getViewFilterForTable(tableView),
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => {
            const itemIds = getItemIdsFromRowKeys(rowKeys, tableView);
            return JSON.stringify({ itemIds: itemIds });
        },
        actionSource: (
            rowKeys: string[],
            tableView: TableView,
            shouldMarkAsPinned: boolean,
            actionSource: ActionSource
        ) => actionSource,
        options: { isCore: true },
    },
    moveConversationToFocusedOrOther: {
        name: (
            conversationRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => {
            return toViewFilter === FocusedViewFilter.Focused
                ? 'TnS_MoveConversationToFocused'
                : 'TnS_MoveConversationToOther';
        },
        customData: (
            conversationRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => [
            conversationRowKeys.length,
            getFocusedFilterForTable(listViewStore.tableViews.get(tableViewId)),
        ],
        cosmosOnlyData: (
            conversationRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => {
            const tableView = listViewStore.tableViews.get(tableViewId);
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(conversationRowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(conversationRowKeys, tableView),
            });
        },
        options: { isCore: true },
        actionSource: (
            conversationRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => actionSource,
    },
    moveItemToFocusedOrOther: {
        name: (
            itemRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => {
            return toViewFilter === FocusedViewFilter.Focused
                ? 'TnS_MoveItemToFocused'
                : 'TnS_MoveItemToOther';
        },
        customData: (
            itemRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => [
            itemRowKeys.length,
            getFocusedFilterForTable(listViewStore.tableViews.get(tableViewId)),
        ],
        cosmosOnlyData: (
            itemRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => {
            const tableView = listViewStore.tableViews.get(tableViewId);
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(itemRowKeys, tableView),
            });
        },
        options: { isCore: true },
        actionSource: (
            itemRowKeys: string[],
            tableViewId: string,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => actionSource,
    },
    alwaysMoveRowToFocusedOrOther: {
        name: (
            rowKeys: string[],
            tableView: TableView,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) =>
            toViewFilter === FocusedViewFilter.Focused
                ? 'TnS_AlwaysMoveToFocused'
                : 'TnS_AlwaysMoveToOther',
        customData: (
            rowKeys: string[],
            tableView: TableView,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => [
            rowKeys.length,
            getFocusedFilterForTable(tableView),
            tableView.tableQuery.listViewType,
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            tableView: TableView,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, tableView),
            });
        },
        options: { isCore: true },
        actionSource: (
            rowKeys: string[],
            tableView: TableView,
            toViewFilter: FocusedViewFilter,
            actionSource: ActionSource
        ) => actionSource,
    },
    addCategories: {
        name: 'TnS_AddCategory',
        customData: (
            rowKeys: string[],
            tableView: TableView,
            categoriesToAdd: string[],
            actionSource: LegacyActionSource
        ) => [
            actionSource,
            rowKeys.length,
            tableView.tableQuery.listViewType,
            getFocusedFilterForTable(tableView),
            getViewFilterForTable(tableView),
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            tableView: TableView,
            categoriesToAdd: string[],
            actionSource: LegacyActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, tableView),
            });
        },
        options: { isCore: true },
    },
    removeCategories: {
        name: 'TnS_RemoveCategory',
        customData: (
            rowKeys: string[],
            tableView: TableView,
            categoriesToRemove: string[],
            actionSource: LegacyActionSource
        ) => [
            actionSource,
            rowKeys.length,
            tableView.tableQuery.listViewType,
            getFocusedFilterForTable(tableView),
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            tableView: TableView,
            categoriesToAdd: string[],
            actionSource: LegacyActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, tableView),
            });
        },
        options: { isCore: true },
    },
    clearCategories: {
        name: 'TnS_ClearCategory',
        customData: (rowKeys: string[], tableView: TableView, actionSource: LegacyActionSource) => [
            actionSource,
            rowKeys.length,
            tableView.tableQuery.listViewType,
            getFocusedFilterForTable(tableView),
        ],
        cosmosOnlyData: (
            rowKeys: string[],
            tableView: TableView,
            actionSource: LegacyActionSource
        ) => {
            return JSON.stringify({
                itemIds: getItemIdsFromRowKeys(rowKeys, tableView),
                conversationIds: getConversationIdsFromRowKeys(rowKeys, tableView),
            });
        },
        options: { isCore: true },
    },
    scheduleItems: {
        name: 'TnS_ScheduleItem',
        customData: (
            rowKeys: string[],
            tableView: TableView,
            actionSource: ActionSource,
            scheduleReturnTime: OwaDate | undefined,
            isScheduled?: boolean
        ) => [
            rowKeys.length,
            tableView.tableQuery.listViewType,
            getFocusedFilterForTable(tableView),
            scheduleReturnTime ? scheduleReturnTime.toISOString() : null,
            isScheduled,
        ],
        options: { isCore: true },
        actionSource: (rowKeys: string[], tableView: TableView, actionSource: ActionSource) =>
            actionSource,
    },
};

function getItemIdsFromRowKeys(rowKeys: string[], tableView: TableView): string[] {
    const itemIds = [];
    for (const rowKey of rowKeys) {
        itemIds.push(...MailRowDataPropertyGetter.getItemIds(rowKey, tableView));
    }
    return itemIds;
}

function getConversationIdsFromRowKeys(rowKeys: string[], tableView: TableView): string[] {
    const conversationIds = [];
    for (const rowKey of rowKeys) {
        conversationIds.push(MailRowDataPropertyGetter.getConversationId(rowKey, tableView));
    }
    return conversationIds;
}

function getFlagIdsToLogFromRowKeys(rowKeys: string[], tableViewId: string): string {
    const itemIds = [];
    const conversationIds = [];
    const tableView = listViewStore.tableViews.get(tableViewId);
    for (const rowKey of rowKeys) {
        itemIds.push(MailRowDataPropertyGetter.getItemIds(rowKey, tableView)[0]);
        conversationIds.push(MailRowDataPropertyGetter.getConversationId(rowKey, tableView));
    }
    return JSON.stringify({ itemIds: itemIds, conversationIds: conversationIds });
}
